const HEART_CHRONICLE_PHASES = [
  "sealed",
  "converging",
  "exposed",
  "sieging",
  "stabilizing",
  "respite",
  "resetting",
] as const;

const HEART_CHRONICLE_OUTCOMES = ["none", "repelled", "stabilized"] as const;

const HEART_FRONT_IDS = [
  "heart-front-1",
  "heart-front-2",
  "heart-front-3",
  "heart-front-4",
  "heart-front-5",
  "heart-front-6",
] as const;

const PROJECTION_KEYS = [
  "schema",
  "chroniclePublicId",
  "phase",
  "heldFrontCount",
  "fronts",
  "convergenceDeadline",
  "lastOutcome",
  "revision",
] as const;
const ROW_KEYS = ["frontId", "status"] as const;
const INVALID_PROJECTION = "invalid-heart-chronicle-public-projection";
const PUBLIC_IDENTIFIER = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,95}$/;

export type HeartChroniclePhase = (typeof HEART_CHRONICLE_PHASES)[number];
export type HeartChronicleOutcome = (typeof HEART_CHRONICLE_OUTCOMES)[number];
export type HeartChronicleFrontId = (typeof HEART_FRONT_IDS)[number];
export type HeartChronicleFrontStatus = "sealed" | "held";

type Six<T> = readonly [T, T, T, T, T, T];

export interface HeartChroniclePublicProjectionRow {
  readonly frontId: HeartChronicleFrontId;
  readonly status: HeartChronicleFrontStatus;
}

export interface HeartChroniclePublicProjection {
  readonly schema: "vaelor-heart-chronicle-public/v1";
  readonly chroniclePublicId: string;
  readonly phase: HeartChroniclePhase;
  readonly heldFrontCount: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  readonly fronts: Six<HeartChroniclePublicProjectionRow>;
  readonly convergenceDeadline: number | null;
  readonly lastOutcome: HeartChronicleOutcome;
  readonly revision: number;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function hasExactPlainDataShape(
  value: unknown,
  expectedKeys: readonly string[],
): value is Record<string, unknown> {
  try {
    if (!isRecord(value) || Object.getPrototypeOf(value) !== Object.prototype) {
      return false;
    }
    const ownNames = Object.getOwnPropertyNames(value);
    if (
      ownNames.length !== expectedKeys.length ||
      Object.getOwnPropertySymbols(value).length !== 0
    ) {
      return false;
    }
    const expected = new Set(expectedKeys);
    for (const name of ownNames) {
      if (!expected.has(name)) return false;
      const descriptor = Object.getOwnPropertyDescriptor(value, name);
      if (
        descriptor === undefined ||
        !("value" in descriptor) ||
        descriptor.enumerable !== true
      ) {
        return false;
      }
    }
    return expectedKeys.every((key) => ownNames.includes(key));
  } catch {
    return false;
  }
}

function hasExactArrayShape(value: unknown, length: number): value is readonly unknown[] {
  try {
    if (
      !Array.isArray(value) ||
      Object.getPrototypeOf(value) !== Array.prototype ||
      value.length !== length
    ) {
      return false;
    }
    const ownNames = Object.getOwnPropertyNames(value);
    if (
      ownNames.length !== length + 1 ||
      Object.getOwnPropertySymbols(value).length !== 0 ||
      !ownNames.includes("length")
    ) {
      return false;
    }
    const lengthDescriptor = Object.getOwnPropertyDescriptor(value, "length");
    if (
      lengthDescriptor === undefined ||
      !("value" in lengthDescriptor) ||
      lengthDescriptor.value !== length ||
      lengthDescriptor.enumerable !== false
    ) {
      return false;
    }
    for (let index = 0; index < length; index += 1) {
      const descriptor = Object.getOwnPropertyDescriptor(value, String(index));
      if (
        descriptor === undefined ||
        !("value" in descriptor) ||
        descriptor.enumerable !== true
      ) {
        return false;
      }
    }
    return true;
  } catch {
    return false;
  }
}

function isNonNegativeSafeInteger(value: unknown): value is number {
  return Number.isSafeInteger(value) && (value as number) >= 0;
}

function isHeartChroniclePhase(value: unknown): value is HeartChroniclePhase {
  return typeof value === "string" && HEART_CHRONICLE_PHASES.includes(value as HeartChroniclePhase);
}

function isHeartChronicleOutcome(value: unknown): value is HeartChronicleOutcome {
  return typeof value === "string" && HEART_CHRONICLE_OUTCOMES.includes(value as HeartChronicleOutcome);
}

function invalidProjection(): never {
  throw new TypeError(INVALID_PROJECTION);
}

export function parseHeartChroniclePublicProjection(
  value: unknown,
): HeartChroniclePublicProjection {
  if (!hasExactPlainDataShape(value, PROJECTION_KEYS)) return invalidProjection();
  if (value.schema !== "vaelor-heart-chronicle-public/v1") return invalidProjection();
  if (
    typeof value.chroniclePublicId !== "string" ||
    !PUBLIC_IDENTIFIER.test(value.chroniclePublicId) ||
    /(?:^|[-_.:])chronicle(?:$|[-_.:])/i.test(value.chroniclePublicId)
  ) {
    return invalidProjection();
  }
  if (!isHeartChroniclePhase(value.phase)) return invalidProjection();
  if (!isNonNegativeSafeInteger(value.heldFrontCount) || value.heldFrontCount > 6) {
    return invalidProjection();
  }
  if (
    value.convergenceDeadline !== null &&
    !isNonNegativeSafeInteger(value.convergenceDeadline)
  ) {
    return invalidProjection();
  }
  if (!isHeartChronicleOutcome(value.lastOutcome)) return invalidProjection();
  if (!isNonNegativeSafeInteger(value.revision)) return invalidProjection();

  const rows = value.fronts;
  if (!hasExactArrayShape(rows, HEART_FRONT_IDS.length)) return invalidProjection();

  let heldFrontCount = 0;
  const fronts = HEART_FRONT_IDS.map((frontId, index) => {
    const row = rows[index];
    if (!hasExactPlainDataShape(row, ROW_KEYS) || row.frontId !== frontId) {
      return invalidProjection();
    }
    if (row.status !== "sealed" && row.status !== "held") return invalidProjection();
    if (row.status === "held") heldFrontCount += 1;
    return Object.freeze({
      frontId,
      status: row.status,
    });
  }) as unknown as Six<HeartChroniclePublicProjectionRow>;

  if (heldFrontCount !== value.heldFrontCount) return invalidProjection();

  return Object.freeze({
    schema: "vaelor-heart-chronicle-public/v1" as const,
    chroniclePublicId: value.chroniclePublicId,
    phase: value.phase,
    heldFrontCount: value.heldFrontCount as 0 | 1 | 2 | 3 | 4 | 5 | 6,
    fronts: Object.freeze(fronts),
    convergenceDeadline: value.convergenceDeadline,
    lastOutcome: value.lastOutcome,
    revision: value.revision,
  });
}

